import React, { useState } from 'react';
import Skeleton from '@mui/material/Skeleton';
import { useHistory } from 'react-router-dom';
import * as S from './styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { toAbsoluteUrl } from "../../../../../_metronic/_helpers";
import ChallengeModal from '../ChallengeModal';
import InviteModal from '../InviteModal';
import {
  trackGamificationInviteModalOpened,
  trackGamificationJusfyPayRedirect,
} from '../../helpers/trackGamificationEvents';
import { formatNextAvailableDate } from '../../helpers/dateHelpers';
import {
  isJuspaySpecialEvent,
  sortGamificationChallenges,
} from 'app/helpers/gamification/challengePresentation';
import { GamificationChallengeEvent } from 'app/helpers/gamification/challengeDomain';
import {
  isChallengeBlockedForAction,
  resolveChallengeStartDestination,
  shouldOpenInviteModal,
} from 'app/helpers/gamification/challengeActions';
import { isCommunityLink, openCommunity } from 'app/services/community';

const ChallengesList = ({
  challenges = [],
  loading = false,
  compact = false,
  onChallengeClick,
  disableInternalFlow = false,
  onChallengeSelect,
  hideLeadingIcon = false,
  onOpenPlanExploration,
}) => {
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isChallengeModalOpen, setIsChallengeModalOpen] = useState(false);
  const [showAllTasks, setShowAllTasks] = useState(false);
  const history = useHistory();

  // Combinar desafios reais com mocks temporários
  const allChallenges = [...challenges];

  // Função para verificar se o desafio é highlight (pontos >= 100)
  const isHighlight = (challenge) => challenge.points >= 100;

  const sortedChallenges = compact
    ? sortGamificationChallenges(allChallenges).slice(0, 3)
    : sortGamificationChallenges(allChallenges);

  // Fora do modo compact, esconde tarefas de menos de 100 XP atrás do "Ver mais tarefas"
  const hiddenTasksCount = compact
    ? 0
    : sortedChallenges.filter((c) => !isHighlight(c)).length;

  const displayChallenges =
    compact || showAllTasks
      ? sortedChallenges
      : sortedChallenges.filter(isHighlight);

  // Função para verificar se deve mostrar o ícone coffee
  const shouldShowCoffee = (event) => ['plan_renewed', 'subuser_created', 'login', 'jus_contabilidade_purchase_completed', 'ia_personalizada_purchase_completed'].includes(event);

  // Função para verificar se deve mostrar o ícone hand (member_get_member)
  const shouldShowHand = (event) =>
    event === GamificationChallengeEvent.MEMBER_GET_MEMBER;

  // Função para verificar se é um desafio JusPay especial
  const isJuspaySpecial = (event) => isJuspaySpecialEvent(event);

  const handleChallengeClick = (challenge) => {
    if (isChallengeBlockedForAction(challenge)) {
      return;
    }

    if (onChallengeClick) {
      onChallengeClick();
    }

    if (onChallengeSelect) {
      onChallengeSelect(challenge);
    }

    if (disableInternalFlow) {
      return;
    }

    setSelectedChallenge(challenge);
    if (shouldOpenInviteModal(challenge)) {
      trackGamificationInviteModalOpened({
        challengeId: challenge.id,
        challengeName: challenge.name,
        challengePoints: challenge.points,
      });
      setIsInviteModalOpen(true);
    } else {
      setIsChallengeModalOpen(true);
    }
  };

  const handleCloseModals = () => {
    setSelectedChallenge(null);
    setIsInviteModalOpen(false);
    setIsChallengeModalOpen(false);
  };

  const handleStartChallenge = () => {
    if (!selectedChallenge) return;
    const destination = resolveChallengeStartDestination(selectedChallenge.event);

    if (isJuspaySpecialEvent(selectedChallenge.event)) {
      trackGamificationJusfyPayRedirect({
        challengeId: selectedChallenge.id,
        challengeName: selectedChallenge.name,
        challengeEvent: selectedChallenge.event,
      });
    }

    if (destination.type === 'external') {
      if (isCommunityLink(destination.url)) {
        openCommunity();
      } else {
        const newWin = window.open(destination.url, '_blank', 'noopener,noreferrer');
        if (newWin) newWin.opener = null;
      }
    } else if (destination.type === 'internal') {
      history.push(destination.path);
    } else if (destination.type === 'modal' && onOpenPlanExploration) {
      onOpenPlanExploration(destination.origin);
    }

    handleCloseModals();
  };

  // Skeleton para loading
  const renderSkeleton = () => (
    <S.Container compact={compact}>
      {[1, 2, 3, 4].slice(0, compact ? 4 : 6).map((i) => (
        <S.ChallengeItem key={i} style={{ cursor: 'default' }}>
          <S.ChallengeInfo>
            <Skeleton variant="text" width="70%" height={20} />
          </S.ChallengeInfo>
          <S.ChallengeXPContainer>
            <Skeleton variant="rounded" width={80} height={36} />
            <Skeleton variant="rounded" width={36} height={36} />
          </S.ChallengeXPContainer>
        </S.ChallengeItem>
      ))}
    </S.Container>
  );

  if (loading) {
    return renderSkeleton();
  }

  return (
    <>
      <S.Container compact={compact}>
        {displayChallenges.map((challenge) => {
          const highlight = isHighlight(challenge);
          const isDone = challenge.user_status === 'finished';
          const isInfinite =
            challenge.event === GamificationChallengeEvent.MEMBER_GET_MEMBER;
          const juspaySpecial = isJuspaySpecial(challenge.event);
          const showCoffee =
            !hideLeadingIcon &&
            shouldShowCoffee(challenge.event) &&
            challenge.points >= 100;
          const showHand = !hideLeadingIcon && shouldShowHand(challenge.event);
          const showGift = !hideLeadingIcon && juspaySpecial;
          const doneHighlight = highlight && isDone && !isInfinite;

          // Formatar data de renovação se o desafio estiver concluído
          const renewalText = isDone && !isInfinite && challenge.user_progress?.next_available_at
            ? formatNextAvailableDate(
                challenge.user_progress.next_available_at,
                juspaySpecial ? 'America/Sao_Paulo' : undefined,
              )
            : '';

          return (
            <S.ChallengeItem
              key={challenge.id}
              highlight={highlight}
              doneHighlight={doneHighlight}
              juspaySpecial={juspaySpecial}
              hideLeadingIcon={hideLeadingIcon}
              onClick={() => handleChallengeClick(challenge)}
              done={isDone && !isInfinite}
              showLines={showCoffee}
            >
              {/* Ícone para JusPay special */}
              {showGift && (
                <S.ChallengeIcon
                  src={toAbsoluteUrl('/media/gamification/done.svg')}
                  float={false}
                  alt="Gift Icon"
                />
              )}

              {/* Ícone coffee para plan_renewed e subuser_created */}
              {/* {showCoffee && (
                <S.ChallengeIcon
                  src={toAbsoluteUrl('/media/gamification/coffee.svg')}
                  float={true}
                  alt="Coffee Icon"
                />
              )} */}

              {/* Ícone para member_get_member */}
              {showHand && (
                <S.ChallengeIcon
                  src={toAbsoluteUrl('/media/gamification/done.svg')}
                  float={false}
                  alt="Referral Icon"
                />
              )}

              <S.ChallengeInfo>
                <S.ChallengeTitle
                  done={isDone && !isInfinite}
                  isMemberGetMember={isInfinite}
                  doneHighlight={doneHighlight}
                >
                  {challenge.name}
                </S.ChallengeTitle>
              </S.ChallengeInfo>

              {isDone && !isInfinite ? (
                <>
                  {renewalText ? (
                    <S.RenewalBadge doneHighlight={doneHighlight}>
                      <S.IconDone
                        src={toAbsoluteUrl('/media/gamification/done.svg')}
                        alt="Done"
                      />
                      Renova {renewalText}
                    </S.RenewalBadge>
                  ) : (
                    <S.ChallengeDone doneHighlight={doneHighlight}>
                      <S.XPValueDone doneHighlight={doneHighlight}>Concluído</S.XPValueDone>
                      <S.IconDone
                        src={toAbsoluteUrl('/media/gamification/done.svg')}
                        alt="Done"
                      />
                    </S.ChallengeDone>
                  )}
                </>
              ) : (
                <S.ChallengeXPContainer>
                  <S.ChallengeXP highlight={highlight} juspaySpecial={juspaySpecial}>
                    <S.XPValue highlight={highlight} juspaySpecial={juspaySpecial}>+ {challenge.points} XP</S.XPValue>
                  </S.ChallengeXP>
                  {challenge.user_status === 'in_progress' &&
                  challenge.target_value &&
                  (!juspaySpecial || challenge.target_value > 1) ? (
                    <S.ProgressBadge highlight={highlight}>
                      {challenge.user_progress?.current_value || 0}/{challenge.target_value}
                    </S.ProgressBadge>
                  ) : (
                    !juspaySpecial && (
                      <S.ButtonGo highlight={highlight}>
                        <ChevronRightIcon sx={{ color: '#01AB7D !important' }} />
                      </S.ButtonGo>
                    )
                  )}
                </S.ChallengeXPContainer>
              )}
            </S.ChallengeItem>
          );
        })}
      </S.Container>

      {hiddenTasksCount > 0 && (
        <S.ShowMoreButton type="button" onClick={() => setShowAllTasks((prev) => !prev)}>
          {showAllTasks ? 'Ver menos tarefas' : 'Ver mais tarefas'}
        </S.ShowMoreButton>
      )}

      {!disableInternalFlow && (
        <>
          <ChallengeModal
            isOpen={isChallengeModalOpen}
            challenge={selectedChallenge}
            onClose={handleCloseModals}
            onStart={handleStartChallenge}
          />

          <InviteModal
            isOpen={isInviteModalOpen}
            challenge={selectedChallenge}
            onClose={handleCloseModals}
          />
        </>
      )}
    </>
  );
};

export default ChallengesList;

